import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import { 
  Smartphone,
  Wifi,
  CreditCard,
  UserPlus,
  ArrowRight,
  Check,
  Zap,
  Shield,
  Clock,
  Leaf,
  Sparkles
} from 'lucide-react';

const HowNFCWorks = () => {
  const navigate = useNavigate();

  const steps = [
    {
      number: '01',
      title: 'Tap Your Card',
      description: 'Simply tap your Piper Card on any NFC-enabled smartphone. No app installation required.',
      icon: CreditCard
    },
    {
      number: '02',
      title: 'Profile Opens Instantly',
      description: 'Your digital profile opens in their browser within milliseconds. Fast and seamless.',
      icon: Smartphone
    },
    {
      number: '03',
      title: 'Contact Saved',
      description: 'They can save your contact, view your links, or connect with you on social media.',
      icon: UserPlus
    }
  ];

  const benefits = [
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Information transfers in under a second'
    },
    {
      icon: Shield,
      title: 'Secure & Private',
      description: 'Your data is protected with encryption'
    },
    {
      icon: Clock,
      title: 'Always Updated',
      description: 'Change your info anytime, instantly reflected'
    },
    {
      icon: Leaf,
      title: 'Sustainable Choice',
      description: 'One smart card for unlimited professional connections'
    }
  ];

  const compatibility = [
    { name: 'iPhone XR and newer', supported: true },
    { name: 'All Android phones with NFC', supported: true },
    { name: 'Samsung Galaxy S8+', supported: true },
    { name: 'Google Pixel 3+', supported: true },
    { name: 'OnePlus 6+', supported: true },
    { name: 'Most smartphones from 2018+', supported: true }
  ];

  return (
    <Layout>
      <SEO 
        title="How NFC Business Cards Work | Piper Card Technology Explained"
        description="Learn how NFC technology powers smart business cards. Understand the tap-to-share technology and why NFC cards are the future of networking."
        keywords={['how NFC works', 'NFC business card technology', 'tap to share', 'smart business cards']}
      />

      <div className="min-h-screen">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Wifi className="w-4 h-4" />
              NFC Technology
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              How <span className="text-amber-400">NFC Cards</span> Work
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-10">
              NFC (Near Field Communication) is the same secure technology used in contactless payments. 
              It enables instant data transfer with a simple tap.
            </p>
            <div className="flex justify-center">
              <Button 
                size="lg" 
                className="bg-amber-500 hover:bg-amber-600 rounded-full px-8"
                onClick={() => navigate('/shop')}
              >
                Get Your NFC Card
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>

        {/* How It Works Steps */}
        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Three Simple Steps
              </h2>
              <p className="text-xl text-gray-600">
                Sharing your contact has never been easier
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {steps.map((step, index) => (
                <div key={index} className="relative">
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-16 left-full w-full h-0.5 bg-gradient-to-r from-amber-500 to-transparent z-0" style={{ width: '100%' }}></div>
                  )}
                  <Card className="border-0 shadow-xl relative z-10 hover:shadow-2xl transition-shadow">
                    <CardContent className="p-8 text-center">
                      <div className="text-6xl font-bold text-amber-500/20 mb-4">{step.number}</div>
                      <div className="w-16 h-16 bg-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                        <step.icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                      <p className="text-gray-600">{step.description}</p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Video/Animation Section */}
        <div className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                  Technology Behind the Tap
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  NFC uses electromagnetic radio fields to enable communication between two devices 
                  when they're brought within close proximity (typically 4cm or less).
                </p>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Our NFC cards contain a small chip that stores your unique profile URL. 
                  When tapped, the smartphone reads this URL and opens your digital profile instantly.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-gray-700">No battery required in the card</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-gray-700">Works through phone cases</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-gray-700">Unlimited taps, no wear and tear</span>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="aspect-square bg-gradient-to-br from-amber-100 to-orange-100 rounded-3xl flex items-center justify-center">
                  <div className="relative">
                    <div className="w-48 h-48 bg-white rounded-2xl shadow-2xl flex items-center justify-center">
                      <CreditCard className="w-24 h-24 text-amber-500" />
                    </div>
                    <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-gray-800 rounded-xl shadow-xl flex items-center justify-center">
                      <Smartphone className="w-16 h-16 text-white" />
                    </div>
                    {/* Animated waves */}
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                      <div className="w-64 h-64 border-2 border-amber-500/30 rounded-full animate-ping"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Why Choose NFC?
              </h2>
              <p className="text-xl text-gray-600">
                The advantages of smart business cards
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <Card key={index} className="border-0 bg-gray-50 hover:bg-amber-50 transition-colors">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <benefit.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-sm text-gray-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Compatibility Section */}
        <div className="py-20 bg-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Universal Compatibility
              </h2>
              <p className="text-xl text-gray-400">
                Works with most modern smartphones
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {compatibility.map((device, index) => (
                <div key={index} className="flex items-center gap-3 bg-gray-800/50 rounded-xl p-4">
                  <Check className="w-5 h-5 text-green-400" />
                  <span>{device.name}</span>
                </div>
              ))}
            </div>

            <p className="text-center text-gray-500 mt-8">
              Don't have NFC? No problem! Every card also includes a QR code for universal compatibility.
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Common Questions
              </h2>
            </div>

            <div className="space-y-6">
              {[
                { q: 'Do I need to install an app?', a: 'No! NFC works natively on all modern smartphones. The recipient simply taps and your profile opens in their browser.' },
                { q: 'How long does the card last?', a: 'NFC cards have no battery and can last 10+ years with normal use. There\'s no limit to how many times you can tap.' },
                { q: 'Is it secure?', a: 'Yes! NFC only works within 4cm range, making it impossible to intercept. Your data is also protected with encryption.' },
                { q: 'Can I update my information?', a: 'Absolutely! Your profile is stored in the cloud. Update it anytime from your dashboard - changes reflect instantly.' },
              ].map((faq, index) => (
                <Card key={index} className="border-0 shadow-md">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-gray-900 mb-2">{faq.q}</h3>
                    <p className="text-gray-600">{faq.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Ready to Try NFC Technology?
            </h2>
            <p className="text-white/90 mb-8 text-lg">
              Get your smart business card and experience the future of networking
            </p>
            <Button 
              size="lg" 
              className="bg-white text-amber-600 hover:bg-gray-100 rounded-full px-10 py-6 text-lg shadow-xl"
              onClick={() => navigate('/shop')}
            >
              Order Your NFC Card
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HowNFCWorks;
